import networkIntelligenceEndpoints from './endpoints/networkIntelligence.endpoints';

// =============================================================================
// Person Details API (for investigation features)
// =============================================================================

/**
 * Cache for person details to avoid repeated API calls.
 */
const personCache = new Map();
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Get person details by ID from SafePath API.
 * @param {number} personId - The person/alien identifier.
 * @returns {Promise<Object|null>} Person info with name, status, etc.
 */
export async function getPersonDetails(personId) {
  // Check cache first
  const cached = personCache.get(personId);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data;
  }

  try {
    const response = await networkIntelligenceEndpoints.getPersonInfo(personId);
    if (response.status === 200) {
      const data = response.data;
      personCache.set(personId, { data, timestamp: Date.now() });
      return data;
    }
    return null;
  } catch (error) {
    console.error(`[getPersonDetails] Error fetching person ${personId}:`, error);
    return null;
  }
}

/**
 * Get multiple person details by IDs (batch fetch).
 * @param {number[]} personIds - Array of person/alien identifiers.
 * @returns {Promise<Map<number, Object>>} Map of personId to person info.
 */
export async function getPersonDetailsBatch(personIds) {
  const results = new Map();
  const uncachedIds = [];

  // Check cache for each ID
  for (const id of personIds) {
    const cached = personCache.get(id);
    if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
      results.set(id, cached.data);
    } else {
      uncachedIds.push(id);
    }
  }

  // Fetch uncached IDs
  if (uncachedIds.length > 0) {
    try {
      const response = await networkIntelligenceEndpoints.getPersonsByPeopleIds(uncachedIds);
      if (response.status === 200 && Array.isArray(response.data)) {
        for (const person of response.data) {
          const id = person.peopleId || person.PeopleId;
          if (id) {
            personCache.set(id, { data: person, timestamp: Date.now() });
            results.set(id, person);
          }
        }
      }
    } catch (error) {
      console.error('[getPersonDetailsBatch] Error:', error);
      // Fall back to individual fetches
      for (const id of uncachedIds) {
        const person = await getPersonDetails(id);
        if (person) {
          results.set(id, person);
        }
      }
    }
  }

  return results;
}

/**
 * Format person name for display.
 * @param {Object} person - Person object with firstName, lastName.
 * @returns {string} Formatted name or "Unknown".
 */
export function formatPersonName(person) {
  if (!person) return 'Unknown';
  const first = person.firstName || person.FirstName || '';
  const last = person.lastName || person.LastName || '';
  if (!first && !last) return `Person #${person.peopleId || person.PeopleId || '?'}`;
  return `${first} ${last}`.trim();
}

/**
 * Get status badge configuration for person status.
 * @param {string} status - Person status string.
 * @returns {Object} Configuration with color and label.
 */
export function getStatusConfig(status) {
  const configs = {
    'Active': { color: '#22C55E', bgColor: '#DCFCE7', label: 'Active' },
    'Compliant': { color: '#22C55E', bgColor: '#DCFCE7', label: 'Compliant' },
    'Non-Compliant': { color: '#EF4444', bgColor: '#FEE2E2', label: 'Non-Compliant' },
    'Inactive': { color: '#6B7280', bgColor: '#F3F4F6', label: 'Inactive' },
    'Pending': { color: '#F59E0B', bgColor: '#FEF3C7', label: 'Pending' },
    'Forfeited': { color: '#991B1B', bgColor: '#FEE2E2', label: 'Forfeited' },
  };
  return configs[status] || { color: '#6B7280', bgColor: '#F3F4F6', label: status || 'Unknown' };
}

/**
 * Build/rebuild the network graph from co-location events.
 * @param {Object} options - Build options.
 * @param {string} [options.timeWindow='30min'] - Time window for co-location grouping.
 * @param {number} [options.precision=3] - Coordinate rounding precision.
 * @param {number} [options.minInteractions=2] - Minimum co-location events to create edge.
 * @returns {Promise<Object|null>} Build result with node/edge counts.
 */
export async function buildNetworkGraph(options = {}) {
  try {
    const payload = {
      timeWindow: options.timeWindow || '30min',
      precision: options.precision || 3,
      minInteractions: options.minInteractions || 2,
    };
    const response = await networkIntelligenceEndpoints.buildGraph(payload);
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('[buildNetworkGraph] Error:', error);
    return null;
  }
}

/**
 * Get complete network analysis for a person.
 * @param {number} personId - The person/alien identifier.
 * @param {boolean} [forceRefresh=false] - Force recalculation, bypassing cache.
 * @returns {Promise<Object|null>} Network analysis with centrality metrics and risk.
 */
export async function getNetworkAnalysis(personId, forceRefresh = false) {
  try {
    const response = await networkIntelligenceEndpoints.getAnalysis(personId, forceRefresh ? { params: { forceRefresh: true } } : undefined);
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      console.log(`[getNetworkAnalysis] Network not built or person ${personId} not in network`);
      return null;
    }
    console.error('[getNetworkAnalysis] Error:', error);
    return null;
  }
}

/**
 * Calculate association risk for a person.
 * @param {number} personId - The person/alien identifier.
 * @param {number[]} [highRiskPersons] - Optional list of known high-risk person IDs.
 * @returns {Promise<Object|null>} Association risk result.
 */
export async function calculateAssociationRisk(personId, highRiskPersons = null) {
  try {
    const payload = {
      personId: personId,
    };
    if (highRiskPersons && highRiskPersons.length > 0) {
      payload.highRiskPersons = highRiskPersons;
    }
    const response = await networkIntelligenceEndpoints.calculateAssociationRisk(payload);
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('[calculateAssociationRisk] Error:', error);
    return null;
  }
}

/**
 * Get connected persons for a subject.
 * @param {number} personId - The person/alien identifier.
 * @param {number} [maxDepth=2] - Maximum connection depth.
 * @returns {Promise<Object|null>} Connected persons data.
 */
export async function getConnectedPersons(personId, maxDepth = 2) {
  try {
    const response = await networkIntelligenceEndpoints.getConnected(personId, { params: { maxDepth } });
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('[getConnectedPersons] Error:', error);
    return null;
  }
}

/**
 * Get network visualization data for force-directed graph rendering.
 * @param {number} personId - The focal person/alien identifier.
 * @param {number} [maxDepth=2] - Maximum depth to include.
 * @returns {Promise<Object|null>} Nodes and links for visualization.
 */
export async function getNetworkVisualization(personId, maxDepth = 2) {
  try {
    const response = await networkIntelligenceEndpoints.getVisualization(personId, { params: { maxDepth } });
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('[getNetworkVisualization] Error:', error);
    return null;
  }
}

/**
 * Get detailed edge information including co-location coordinates.
 * @param {number} person1Id - First person ID.
 * @param {number} person2Id - Second person ID.
 * @returns {Promise<Object|null>} Edge details with co-location locations.
 */
export async function getEdgeDetails(person1Id, person2Id) {
  try {
    const response = await networkIntelligenceEndpoints.getEdgeDetails({ params: { person1Id, person2Id } });
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    if (error.response?.status === 404) {
      console.log(`[getEdgeDetails] No connection between ${person1Id} and ${person2Id}`);
      return null;
    }
    console.error('[getEdgeDetails] Error:', error);
    return null;
  }
}

/**
 * Get all members of a specific community with statistics.
 * @param {number} communityId - The community ID.
 * @returns {Promise<Object|null>} Community stats with member list.
 */
export async function getCommunityMembers(communityId) {
  try {
    const response = await networkIntelligenceEndpoints.getCommunityMembers(communityId);
    if (response.status === 200) {
      return response.data;
    }
    return null;
  } catch (error) {
    console.error('[getCommunityMembers] Error:', error);
    return null;
  }
}

// =============================================================================
// Display Configuration Helpers
// =============================================================================

/**
 * Get risk level display configuration.
 * @param {string} level - Risk level (low/medium/high/critical).
 * @returns {Object} Configuration with color, bgColor, and label.
 */
export function getRiskLevelConfig(level) {
  const configs = {
    low: { color: '#22C55E', bgColor: '#DCFCE7', label: 'Low Risk' },
    medium: { color: '#F59E0B', bgColor: '#FEF3C7', label: 'Medium Risk' },
    high: { color: '#EF4444', bgColor: '#FEE2E2', label: 'High Risk' },
    critical: { color: '#991B1B', bgColor: '#FEE2E2', label: 'Critical Risk' },
    unknown: { color: '#6B7280', bgColor: '#F3F4F6', label: 'Unknown' },
  };
  return configs[level] || configs.unknown;
}

/**
 * Get node type display configuration for visualization.
 * @param {boolean} isFocal - Whether this is the focal node.
 * @param {number} depth - Connection depth from focal node.
 * @returns {Object} Configuration with color, size, and label.
 */
export function getNodeTypeConfig(isFocal, depth) {
  if (isFocal) {
    return { color: '#3B82F6', size: 16, label: 'Subject' };
  }
  const configs = {
    1: { color: '#8B5CF6', size: 12, label: 'Direct Connection' },
    2: { color: '#EC4899', size: 8, label: 'Indirect Connection' },
  };
  return configs[depth] || { color: '#9CA3AF', size: 6, label: `Depth ${depth}` };
}

/**
 * Get edge strength display configuration.
 * @param {number} weight - Edge weight (0-1).
 * @returns {Object} Configuration with color and width.
 */
export function getEdgeConfig(weight) {
  if (weight >= 0.7) {
    return { color: '#EF4444', width: 3, label: 'Strong' };
  } else if (weight >= 0.4) {
    return { color: '#F59E0B', width: 2, label: 'Moderate' };
  }
  return { color: '#9CA3AF', width: 1, label: 'Weak' };
}

/**
 * Format centrality value for display.
 * @param {number} value - Centrality value (0-1).
 * @returns {string} Formatted percentage string.
 */
export function formatCentrality(value) {
  if (value === null || value === undefined) return 'N/A';
  return `${(value * 100).toFixed(1)}%`;
}

/**
 * Format risk score for display.
 * @param {number} score - Risk score (0-1).
 * @returns {string} Formatted percentage string.
 */
export function formatRiskScore(score) {
  if (score === null || score === undefined) return 'N/A';
  return `${(score * 100).toFixed(0)}%`;
}

/**
 * Get risk factor display text.
 * @param {string} factor - Risk factor code.
 * @returns {string} Human-readable description.
 */
export function getRiskFactorDisplay(factor) {
  const displays = {
    'not_in_network': 'Not in association network',
    'no_risk_factors': 'No risk factors identified',
    'highly_connected': 'Highly connected in network',
    'bridge_node': 'Acts as bridge between groups',
    'influential_position': 'Influential network position',
    'high_risk_community': 'In high-risk community cluster',
    'moderate_risk_community': 'In moderate-risk community cluster',
    'network_not_built': 'Network analysis not available',
  };

  // Handle dynamic factors like "2_high_risk_connections"
  const hrMatch = factor.match(/^(\d+)_high_risk_connections$/);
  if (hrMatch) {
    const count = hrMatch[1];
    return `${count} high-risk connection${count === '1' ? '' : 's'}`;
  }

  return displays[factor] || factor.replace(/_/g, ' ');
}

/**
 * Calculate community color based on community ID.
 * @param {number} communityId - Community identifier.
 * @returns {string} Hex color for the community.
 */
export function getCommunityColor(communityId) {
  const colors = [
    '#3B82F6', // blue
    '#22C55E', // green
    '#F59E0B', // amber
    '#EF4444', // red
    '#8B5CF6', // purple
    '#EC4899', // pink
    '#14B8A6', // teal
    '#6366F1', // indigo
    '#F97316', // orange
    '#84CC16', // lime
  ];
  return colors[communityId % colors.length];
}
